/* Inclusive Barista — hero: the pink ramp as a physical object (three.js).
   One wedge, real soft shadow on a white floor, matte painted surface.
   Scroll orbits the camera: state 1 (headline) -> state 2 (turn) -> state 3 (the wall: Cycling Coffee Club).
   Variants only change composition: camera frames, light, where the headline lives.
   Preview helpers: ?hero=1..4 picks a variant, ?p=0..1 freezes the story at a point. */
(function () {
  'use strict';
  var root = document.querySelector('[data-rh]');
  if (!root || typeof THREE === 'undefined') return;

  var qs = new URLSearchParams(location.search);
  var variant = parseInt(qs.get('hero') || root.getAttribute('data-variant') || '1', 10);
  if (!(variant >= 1 && variant <= 4)) variant = 1;
  root.setAttribute('data-variant', String(variant));
  var forceP = qs.has('p') ? Math.max(0, Math.min(1, parseFloat(qs.get('p')))) : null;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var canvas = root.querySelector('.rh__canvas');
  var stage = root.querySelector('.rh__stage');
  var title = root.querySelector('.rh__title');
  var href = root.getAttribute('data-href') || '/pages/cycling-coffee-club';
  var markUrl = root.getAttribute('data-mark') || 'ramp-mark.svg';
  var slopeLines = (root.getAttribute('data-slope-text') || 'The future is|inclusive.').split('|');
  var wallText = root.getAttribute('data-wall-text') || '';
  var wallLines = wallText ? wallText.split('|') : [];
  if (qs.get('nav')) root.setAttribute('data-nav', qs.get('nav'));

  /* ------------------------------------------------------------------ composition */
  // az: 0 = camera in front of the long side, + toward the tall end. el: degrees above floor.
  // dist: camera distance. t*: look-at point. sx/sy: shift of the object on screen (fraction of w/h).
  var F = function (az, el, dist, tx, ty, tz, sx, sy) {
    return { az: az, el: el, dist: dist, tx: tx, ty: ty, tz: tz, sx: sx, sy: sy };
  };
  var VARIANTS = {
    // 1 — Monolith: three-quarter view from above-left, ramp huge and bleeding off the right edge, type under it.
    1: { frames: [F(-38, 21, 7.6, 0.4, 0.55, 0, 0.2, 0.12), F(-6, 15, 8.2, 0.6, 0.6, 0, 0.12, 0.02), F(86, 8, 4.7, 2.5, 0.68, 0, 0, -0.02)], slope: 'plain' },
    // 2 — Surface: the headline is printed on the slope itself; high camera, ramp runs diagonally.
    2: { frames: [F(-4, 52, 7.4, 0.2, 0.2, 0, 0.03, 0.0), F(24, 24, 7.8, 0.6, 0.5, 0, 0.05, -0.02), F(88, 8, 4.2, 2.5, 0.68, 0, 0.0, -0.02)], slope: 'headline' },
    // 3 — Fragment: extreme crop. Ramp pushes in from the bottom, headline across the top, ramp crosses its axis.
    3: { frames: [F(-62, 13, 6.2, 0.9, 0.5, 0, 0.1, -0.37), F(-20, 14, 6.8, 0.9, 0.5, 0, 0.05, -0.2), F(84, 8, 3.5, 2.5, 0.62, 0, 0.0, -0.1)], slope: 'plain' },
    // 4 — Elevation: flat side view, pure silhouette; the headline sits in the empty air above the slope.
    4: { frames: [F(-12, 15, 8.0, 0.2, 0.55, 0, 0.2, -0.15), F(30, 8, 9.2, 0.4, 0.65, 0, 0.03, -0.08), F(88, 6, 5.3, 2.5, 0.66, 0, 0.0, -0.04)], slope: 'mark' }
  };
  var V = VARIANTS[variant];
  var FOV = 24;

  /* ------------------------------------------------------------------ renderer */
  THREE.ColorManagement.legacyMode = false;
  var renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (e) {
    root.classList.add('rh--static');
    return;
  }
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.setClearColor(0xffffff, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.VSMShadowMap;

  var scene = new THREE.Scene();
  var camera = new THREE.PerspectiveCamera(FOV, 1.6, 0.1, 80);

  /* ------------------------------------------------------------------ material (painted, slightly worn) */
  var PINK = new THREE.Color('#ff679a');
  function patch(mat, opts) {
    opts = opts || {};
    mat.onBeforeCompile = function (sh) {
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
        .replace('#include <project_vertex>', '#include <project_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', [
          '#include <common>',
          'varying vec3 vWPos;',
          'float h31(vec3 p){ p = fract(p * 0.1031); p += dot(p, p.yzx + 33.33); return fract((p.x + p.y) * p.z); }',
          'float vn(vec3 p){ vec3 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);',
          ' return mix(mix(mix(h31(i),h31(i+vec3(1,0,0)),f.x), mix(h31(i+vec3(0,1,0)),h31(i+vec3(1,1,0)),f.x), f.y),',
          '            mix(mix(h31(i+vec3(0,0,1)),h31(i+vec3(1,0,1)),f.x), mix(h31(i+vec3(0,1,1)),h31(i+vec3(1,1,1)),f.x), f.y), f.z); }'
        ].join('\n'))
        .replace('#include <color_fragment>', [
          '#include <color_fragment>',
          'float big = vn(vWPos * 1.4) * 0.6 + vn(vWPos * 4.0) * 0.4;',
          'float grain = h31(floor(vWPos * 260.0));',
          'diffuseColor.rgb *= 0.955 + 0.075 * big;',
          'diffuseColor.rgb *= 0.985 + 0.03 * grain;',
          // dirt / wear: darker, slightly greyer toward the floor, broken up by noise
          'float low = smoothstep(0.42, 0.0, vWPos.y) * (0.45 + 0.55 * vn(vWPos * 6.0 + 3.0));',
          'diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.80, 0.72, 0.75), low * 0.55);',
          // used surface: a faint worn lane down the middle of the slope, soft streaks on the sides
          'float lane = smoothstep(0.62, 0.0, abs(vWPos.z)) * smoothstep(0.2, 0.9, vWPos.y) * (0.5 + 0.5 * vn(vec3(vWPos.x * 2.0, 0.0, vWPos.z * 9.0)));',
          'diffuseColor.rgb *= 1.0 - 0.07 * lane;',
          'float streak = vn(vec3(vWPos.x * 7.0, vWPos.y * 0.6, vWPos.z * 7.0));',
          'diffuseColor.rgb *= 0.97 + 0.05 * streak;'
        ].join('\n'));
    };
    return mat;
  }

  var bodyMat = patch(new THREE.MeshStandardMaterial({ color: PINK, roughness: 0.92, metalness: 0 }));

  /* ------------------------------------------------------------------ geometry */
  var L = 5, H = 1.35, W = 2.0, LIP = 0.09;
  var BEV = 0.035;
  // the bevel grows the outline outward, so inset the profile to land on L x H x W exactly
  var cosT = Math.cos(Math.atan2(H - LIP, L));
  function slopeY(x) { return LIP + (H - LIP) * (x + L / 2) / L - BEV / cosT; }
  var shape = new THREE.Shape();
  shape.moveTo(-L / 2 + BEV, BEV);
  shape.lineTo(L / 2 - BEV, BEV);
  shape.lineTo(L / 2 - BEV, slopeY(L / 2 - BEV));
  shape.lineTo(-L / 2 + BEV, slopeY(-L / 2 + BEV));
  shape.closePath();
  var geo = new THREE.ExtrudeGeometry(shape, { depth: W - BEV * 2, bevelEnabled: true, bevelThickness: BEV, bevelSize: BEV, bevelSegments: 3, curveSegments: 1 });
  geo.translate(0, 0, -(W - BEV * 2) / 2);
  var ramp = new THREE.Mesh(geo, bodyMat);
  ramp.castShadow = true;
  ramp.receiveShadow = true;
  scene.add(ramp);

  var slopeLen = Math.sqrt(L * L + (H - LIP) * (H - LIP));
  var theta = Math.atan2(H - LIP, L);
  var slopeDir = new THREE.Vector3(Math.cos(theta), Math.sin(theta), 0);
  var slopeN = new THREE.Vector3(-Math.sin(theta), Math.cos(theta), 0);

  /* ------------------------------------------------------------------ floor: only catches the shadow */
  var floorMat = new THREE.ShadowMaterial({ opacity: 0.24, color: 0x2a1018 });
  var floor = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);

  // contact shadow: tight, dark line where the object meets the floor
  var cc = document.createElement('canvas');
  cc.width = cc.height = 256;
  var cx = cc.getContext('2d');
  var g = cx.createRadialGradient(128, 128, 20, 128, 128, 128);
  g.addColorStop(0, 'rgba(30,8,18,0.55)');
  g.addColorStop(0.55, 'rgba(30,8,18,0.16)');
  g.addColorStop(1, 'rgba(30,8,18,0)');
  cx.fillStyle = g;
  cx.fillRect(0, 0, 256, 256);
  var contact = new THREE.Mesh(
    new THREE.PlaneGeometry(L + 1.4, W + 1.4),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cc), transparent: true, depthWrite: false })
  );
  contact.rotation.x = -Math.PI / 2;
  contact.position.y = 0.002;
  scene.add(contact);

  /* ------------------------------------------------------------------ light */
  scene.add(new THREE.HemisphereLight(0xffffff, 0xd8b8c4, 0.36));
  var key = new THREE.DirectionalLight(0xfff2ea, 1.0);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.left = -7;
  key.shadow.camera.right = 7;
  key.shadow.camera.top = 7;
  key.shadow.camera.bottom = -7;
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 30;
  key.shadow.radius = 9;
  key.shadow.blurSamples = 12;
  key.shadow.bias = -0.0004;
  scene.add(key);
  scene.add(key.target);
  var fill = new THREE.DirectionalLight(0xffe3ea, 0.22);
  fill.position.set(6, 3, 5);
  scene.add(fill);

  /* ------------------------------------------------------------------ decals (printed type that lives on the object) */
  function decal(w, h, px, draw, opts) {
    opts = opts || {};
    var c = document.createElement('canvas');
    c.width = Math.round(w * px);
    c.height = Math.round(h * px);
    var ctx = c.getContext('2d');
    draw(ctx, c.width, c.height);
    var tex = new THREE.CanvasTexture(c);
    tex.encoding = THREE.sRGBEncoding;
    tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    var m = patch(new THREE.MeshStandardMaterial({
      map: tex, transparent: true, roughness: 0.9, metalness: 0,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, depthWrite: false
    }));
    var mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), m);
    mesh.receiveShadow = true;
    return mesh;
  }

  var INK = '#ffffff';
  var FONT = 'Inter, "Helvetica Neue", Arial, sans-serif';
  var SERIF = '"Instrument Serif", Georgia, serif';

  var decals = {};

  function buildDecals() {
    var i;
    // --- slope
    var sw = slopeLen * 0.96, sh = W * 0.9;
    var slopeMesh = null;
    if (V.slope === 'headline') {
      slopeMesh = decal(sw, sh, 380, function (ctx, cw, ch) {
        ctx.fillStyle = INK;
        ctx.textBaseline = 'alphabetic';
        var lines = slopeLines.map(function (s) { return s.toUpperCase(); });
        var size = ch * 0.34;
        ctx.font = '800 ' + size + 'px ' + FONT;
        ctx.letterSpacing = (-size * 0.045) + 'px';
        var widest = 0;
        lines.forEach(function (t) { widest = Math.max(widest, ctx.measureText(t).width); });
        var k = Math.min(1, (cw * 0.94) / widest);
        size *= k;
        ctx.font = '800 ' + size + 'px ' + FONT;
        ctx.letterSpacing = (-size * 0.045) + 'px';
        var lh = size * 0.9;
        var total = lh * (lines.length - 1) + size * 0.72;
        var y0 = (ch - total) / 2 + size * 0.72;
        lines.forEach(function (t, n) { ctx.fillText(t, cw * 0.03, y0 + n * lh); });
      });
    } else if (V.slope === 'mark') {
      slopeMesh = decal(sw, sh, 380, function (ctx, cw, ch) {
        if (decals.markImg) {
          var iw = cw * 0.8, ih = iw * (358 / 798);
          ctx.globalAlpha = 0.98;
          ctx.drawImage(decals.markImg, (cw - iw) / 2, (ch - ih) / 2, iw, ih);
        }
      });
    }
    if (slopeMesh) {
      var bs = new THREE.Matrix4().makeBasis(slopeDir, new THREE.Vector3(0, 0, -1), slopeN);
      slopeMesh.setRotationFromMatrix(bs);
      slopeMesh.position.set(0, (H + LIP) / 2, 0).addScaledVector(slopeN, 0.006);
      scene.add(slopeMesh);
      decals.slope = slopeMesh;
    }

    if (!wallLines.length) return; // nothing is printed on the sides or the tall wall

    // --- long sides: a small physical label running parallel to the slope
    var tw = 3.1, th = 0.42;
    function sideTexture(ctx, cw, ch) {
      ctx.fillStyle = INK;
      ctx.textBaseline = 'middle';
      var size = ch * 0.62;
      ctx.font = '800 ' + size + 'px ' + FONT;
      ctx.letterSpacing = (-size * 0.03) + 'px';
      var t = wallLines.join(' ').toUpperCase();
      var wdt = ctx.measureText(t).width;
      var k = Math.min(1, (cw * 0.97) / wdt);
      ctx.font = '800 ' + size * k + 'px ' + FONT;
      ctx.letterSpacing = (-size * k * 0.03) + 'px';
      ctx.textAlign = 'right';
      ctx.fillText(t, cw * 0.985, ch * 0.54);
    }
    [1, -1].forEach(function (side) {
      var m = decal(tw, th, 300, sideTexture);
      var b = new THREE.Matrix4().makeBasis(slopeDir, slopeN, new THREE.Vector3(0, 0, 1));
      m.setRotationFromMatrix(b);
      if (side < 0) m.rotateY(Math.PI);
      // anchor: close to the tall end, a hand below the slope line
      var along = slopeLen / 2 - 0.2 - tw / 2;
      var p = new THREE.Vector3(0, (H + LIP) / 2, 0).addScaledVector(slopeDir, along).addScaledVector(slopeN, -0.42);
      p.z = side * (W / 2 + 0.003);
      m.position.copy(p);
      if (side < 0) {
        // mirrored side must still read left-to-right from outside: flip along-slope placement
        m.position.copy(new THREE.Vector3(0, (H + LIP) / 2, 0).addScaledVector(slopeDir, along).addScaledVector(slopeN, -0.42));
        m.position.z = -(W / 2 + 0.003);
        m.rotation.set(0, 0, 0);
        m.setRotationFromMatrix(new THREE.Matrix4().makeBasis(slopeDir.clone().negate(), slopeN, new THREE.Vector3(0, 0, -1)));
        m.rotateZ(Math.PI);
        m.position.copy(new THREE.Vector3(0, (H + LIP) / 2, 0).addScaledVector(slopeDir, along).addScaledVector(slopeN, -0.42));
        m.position.z = -(W / 2 + 0.003);
      }
      scene.add(m);
      decals['side' + side] = m;
    });

    // --- the tall end wall: Cycling Coffee Club, large, physical
    var wall = decal(W, H, 520, function (ctx, cw, ch) {
      ctx.fillStyle = INK;
      ctx.textBaseline = 'alphabetic';
      var n = wallLines.length;
      var size = ch * 0.3;
      var pad = cw * 0.075;
      var lh = size * 0.88;
      var total = lh * (n - 1);
      var y = (ch - total) / 2 + size * 0.34;
      wallLines.forEach(function (t, k) {
        var up = t.toUpperCase();
        if (/coffee/i.test(t)) {
          ctx.font = 'italic 400 ' + size * 1.12 + 'px ' + SERIF;
          ctx.letterSpacing = (-size * 0.02) + 'px';
          ctx.fillText(t, pad, y + k * lh);
        } else {
          ctx.font = '800 ' + size + 'px ' + FONT;
          ctx.letterSpacing = (-size * 0.045) + 'px';
          ctx.fillText(up, pad, y + k * lh);
        }
      });
    });
    wall.rotation.y = Math.PI / 2;
    wall.position.set(L / 2 + 0.004, H / 2, 0);
    scene.add(wall);
    decals.wall = wall;
  }

  /* ------------------------------------------------------------------ fit the HTML headline to the screen */
  function fitTitle() {
    if (!title) return;
    var fit = parseFloat(getComputedStyle(title).getPropertyValue('--rh-fit')) || 0.9;
    var lines = title.querySelectorAll('.rh__line');
    title.style.setProperty('--rh-fs', '100px');
    var widest = 0;
    lines.forEach(function (l) { widest = Math.max(widest, l.getBoundingClientRect().width); });
    if (!widest) return;
    var target = stage.clientWidth * fit;
    title.style.setProperty('--rh-fs', (100 * target / widest).toFixed(2) + 'px');
  }

  /* ------------------------------------------------------------------ story */
  var progress = 0, shown = 0;
  var pointer = { x: 0, y: 0, sx: 0, sy: 0 };

  function readScroll() {
    if (forceP !== null) return forceP;
    var r = root.getBoundingClientRect();
    var span = root.offsetHeight - window.innerHeight;
    if (span <= 0) return 0;
    return Math.max(0, Math.min(1, -r.top / span));
  }
  function sstep(a, b, x) { var t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function mix(fa, fb, t) {
    var o = {};
    for (var k in fa) o[k] = lerp(fa[k], fb[k], t);
    return o;
  }
  function frameAt(p) {
    var f = V.frames;
    if (p < 0.5) return mix(f[0], f[1], sstep(0.03, 0.47, p));
    return mix(f[1], f[2], sstep(0.53, 0.97, p));
  }

  var size = { w: 0, h: 0 };
  function resize() {
    var w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h) return;
    size.w = w; size.h = h;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    dirty = true;
    fitTitle();
  }

  var pos = new THREE.Vector3(), tgt = new THREE.Vector3();
  function render(dt) {
    var p = shown;
    var f = frameAt(p);
    var asp = size.w / size.h;
    var fitK = Math.pow(Math.max(1, 1.7 / asp), 0.5);
    var az = f.az + pointer.sx * 3.2;
    var el = f.el - pointer.sy * 1.8;
    var d = f.dist * fitK;
    var ar = THREE.MathUtils.degToRad(az), er = THREE.MathUtils.degToRad(el);
    tgt.set(f.tx, f.ty, f.tz);
    pos.set(Math.sin(ar) * Math.cos(er), Math.sin(er), Math.cos(ar) * Math.cos(er)).multiplyScalar(d).add(tgt);
    camera.position.copy(pos);
    camera.lookAt(tgt);
    camera.setViewOffset(size.w, size.h, -f.sx * size.w, f.sy * size.h, size.w, size.h);
    camera.updateProjectionMatrix();

    // studio light rides a little with the camera so every face gets read
    var la = THREE.MathUtils.degToRad(az - lerp(60, 28, sstep(0.5, 1, p)));
    key.position.set(Math.sin(la) * 9, 10, Math.cos(la) * 9);
    key.target.position.set(0, 0, 0);

    // headline leaves as the camera turns away (physical type stays)
    if (title) {
      var out = sstep(0.0, 0.2, p);
      title.style.opacity = (1 - out).toFixed(3);
      title.style.transform = 'translate3d(0,' + (-out * 4).toFixed(2) + 'vh,0)';
    }
    root.style.setProperty('--rh-p', p.toFixed(3));
    renderer.render(scene, camera);
  }

  /* wall hit-test: navigation embedded in the object */
  var ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
  function overWall(ev) {
    if (!decals.wall || shown < 0.72) return false;
    var r = canvas.getBoundingClientRect();
    ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    return ray.intersectObject(decals.wall).length > 0;
  }
  canvas.addEventListener('pointermove', function (ev) {
    pointer.x = (ev.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (ev.clientY / window.innerHeight) * 2 - 1;
    canvas.style.cursor = overWall(ev) ? 'pointer' : '';
  });
  canvas.addEventListener('click', function (ev) { if (overWall(ev)) location.href = href; });
  window.addEventListener('pointermove', function (ev) {
    pointer.x = (ev.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (ev.clientY / window.innerHeight) * 2 - 1;
  }, { passive: true });

  var last = performance.now(), running = true, dirty = true, onScreen = true;
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (e) { onScreen = e[0].isIntersecting; if (onScreen) dirty = true; }, { rootMargin: '10% 0px' }).observe(stage);
  }
  function tick(now) {
    var dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    progress = readScroll();
    var k = reduce || forceP !== null ? 1 : 1 - Math.exp(-dt / 0.085);
    var prev = shown, px = pointer.sx, py = pointer.sy;
    shown += (progress - shown) * k;
    if (Math.abs(progress - shown) < 0.0003) shown = progress;
    var pk = reduce ? 0 : 1 - Math.exp(-dt / 0.35);
    pointer.sx += (pointer.x - pointer.sx) * pk;
    pointer.sy += (pointer.y - pointer.sy) * pk;
    if (Math.abs(pointer.x - pointer.sx) < 0.001) pointer.sx = pointer.x;
    if (Math.abs(pointer.y - pointer.sy) < 0.001) pointer.sy = pointer.y;
    // skip the GPU entirely while nothing moves or the stage is off screen
    if (onScreen && (dirty || prev !== shown || px !== pointer.sx || py !== pointer.sy)) {
      render(dt);
      dirty = false;
    }
    if (running) requestAnimationFrame(tick);
  }

  document.addEventListener('visibilitychange', function () {
    running = !document.hidden;
    if (running) { last = performance.now(); requestAnimationFrame(tick); }
  });
  window.addEventListener('resize', resize);

  /* ------------------------------------------------------------------ boot */
  var fontsReady = (document.fonts && document.fonts.load)
    ? Promise.all([
        document.fonts.load('800 100px Inter'),
        document.fonts.load('italic 400 100px "Instrument Serif"')
      ]).catch(function () {})
    : Promise.resolve();
  var markReady = new Promise(function (res) {
    var img = new Image();
    img.onload = function () { decals.markImg = img; res(); };
    img.onerror = res;
    img.src = markUrl;
  });
  Promise.all([fontsReady, markReady]).then(function () {
    buildDecals();
    resize();
    shown = progress = readScroll();
    root.classList.add('is-ready');
    requestAnimationFrame(tick);
  });
  window.__rh = { camera: camera, scene: scene, V: V };
})();
